import { useCallback, useEffect, useMemo } from "react"

import { Formik, useFormikContext } from "formik"
import { isEqual } from "lodash"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { SearchIcon } from "@l2r-front/l2r-icons"
import { I18N_NAMESPACE } from "@l2r-front/l2r-shared-campaign"
import { L2RButton, ScrollableLayout, useIsMobileDevice } from "@l2r-front/l2r-ui"

import { validationSchema } from "./CampaignDeliverForm.schema"
import * as Styled from "./CampaignDeliverForm.styled"

interface FormChangeObserverProps {
    initialValues: any
    onChange?: (_hasChanged: boolean) => void
}

const FormChangeObserver = ({ initialValues, onChange }: FormChangeObserverProps) => {
    const { values } = useFormikContext()

    const compareValues = useCallback(
        (
            values,
            initialValues
        ): boolean => {
            const { tags: initialTags, ...initialFields } = initialValues
            const { tags, ...fields } = values

            const areInitialTags = Boolean(
                (tags?.length === initialTags?.length) &&
                tags?.every((tag: string) => initialTags?.includes(tag))
            )
            const areInitialFields: boolean = isEqual(fields, initialFields)

            return areInitialFields && areInitialTags
        },
        []
    )

    useEffect(() => {
        onChange?.(!compareValues(values, initialValues))
    }, [values, initialValues, compareValues, onChange])

    return null
}

export interface CampaignDeliverFormProps {
    className?: string
    formConfiguration: any
    projectTags?: string[]
    isSubmitting: boolean
    onCancel: () => void
    onChange?: (_hasChanged: boolean) => void
    onSubmit: (values: any) => void
}

export const CampaignDeliverForm = (props: CampaignDeliverFormProps) => {
    const {
        className,
        formConfiguration,
        projectTags = [],
        isSubmitting,
        onCancel,
        onChange,
        onSubmit,
    } = props

    const { t } = useTranslation(I18N_NAMESPACE)
    const isMobile = useIsMobileDevice()

    const initialValues = useMemo(() => {
        return {
            delivery_action: formConfiguration.deliverActions.options[0]?.value || "",
            tags_to_add: [],
            tags_to_delete: [],
        }
    }, [formConfiguration])

    const handleSubmit = useCallback((values) => {
        onSubmit(values)
    }, [onSubmit])

    return <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={validationSchema()}
    >
        {({ errors, values, setFieldValue }) => {
            const hasErrors = Object.values(errors).some(error => !!error)
            return <Styled.Form>
                <FormChangeObserver initialValues={initialValues} onChange={onChange} />
                <ScrollableLayout
                    className={className}
                    content={
                        <Styled.FormContent isMobile={isMobile}>
                            <Styled.FieldsWrapper isMobile={isMobile}>
                                <Styled.Select
                                    id="delivery_action"
                                    name="delivery_action"
                                    value={values.delivery_action || ""}
                                    label={t(formConfiguration.namespace, formConfiguration.deliverActions.fieldName)}>
                                    {formConfiguration.deliverActions.options.map(action => {
                                        return (
                                            <Styled.MenuItem key={action.value} value={action.value}>
                                                {t(formConfiguration.namespace, action.labelKey)}
                                            </Styled.MenuItem>
                                        )
                                    })}
                                </Styled.Select>
                                {formConfiguration.tags?.tagsToAdd && <Styled.TagsAutocomplete
                                    name="tags_to_add"
                                    label={t(formConfiguration.namespace, formConfiguration.tags.tagsToAdd)}
                                    variant="outlined"
                                    tags={values.tags_to_add}
                                    existingTags={projectTags.filter(tag => !values.tags_to_delete?.includes(tag))}
                                    onChange={(v: any) => setFieldValue("tags_to_add", v)}
                                />}
                                {formConfiguration.tags?.tagsToDelete && <Styled.TagFilter
                                    color="error"
                                    variant="outlined"
                                    id="antiTags-filters-select"
                                    disableClearable
                                    filterSelectedOptions
                                    label={t(formConfiguration.namespace, formConfiguration.tags.tagsToDelete)}
                                    name="tags_to_delete"
                                    options={projectTags.filter(tag => !values.tags_to_add?.includes(tag))}
                                    defaultValue={initialValues?.tags_to_delete}
                                    setFieldValue={setFieldValue}
                                    popupIcon={<SearchIcon />}
                                    noOptionsText={t(I18N_NAMESPACE, "forms.campaignDeliverForm.tags.noOptions")}
                                />}
                            </Styled.FieldsWrapper>
                        </Styled.FormContent>}
                    buttons={
                        <>
                            <L2RButton
                                id="cancel-button"
                                onClick={() => {
                                    onCancel()
                                }}
                                variant="tertiary"
                            >
                                {t(I18N_NAMESPACE, "forms.campaignDeliverForm.buttons.cancel")}
                            </L2RButton>
                            <Styled.SubmitButton
                                id="submit-button"
                                type="submit"
                                disabled={hasErrors || isSubmitting}
                            >
                                {t(I18N_NAMESPACE, "forms.campaignDeliverForm.buttons.save")}
                            </Styled.SubmitButton>
                        </>
                    }
                />
            </Styled.Form>
        }}
    </Formik >
}
